import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, effect, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CrwdButton, CrwdIcon, CrwdModal } from '@crewdle/ui-kit';

import { OnboardingTourService, TourExit } from 'src/app/services/onboarding-tour/onboarding-tour.service';

export interface TourSlide {
  section: 'chats' | 'topics' | 'agents' | 'teams';
  /** Same icon as the section's sidebar entry. */
  icon: string;
}

/** One slide per section, in sidebar order; copy is `sections.<section>.title|explain`. */
export const TOUR_SLIDES: readonly TourSlide[] = [
  { section: 'chats', icon: 'message-circle' },
  { section: 'topics', icon: 'tag' },
  { section: 'agents', icon: 'sparkles' },
  { section: 'teams', icon: 'briefcase-outline' },
];

/**
 * The concepts tour: a crwd-modal (focus trap, scroll lock, Esc, aria from the
 * kit) holding a four-slide carousel — Chats, Topics, Agents, Teams. Back /
 * Next, dots, Skip, and "Get started" on the last slide; ← / → move between
 * slides. Every way out (Get started, Skip, Esc, ✕) records it as seen;
 * "Get started" is told apart from the others (TourExit) for the Help guide.
 * Opened by OnboardingTourService: automatically for a new user, or from the
 * user menu's "Take the tour".
 */
@Component({
  selector: 'app-onboarding-tour',
  standalone: true,
  imports: [TranslateModule, CrwdButton, CrwdIcon, CrwdModal],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <crwd-modal
      [open]="tour.open()"
      (openChange)="onOpenChange($event)"
      [title]="'sections.tour.title' | translate"
      [closeAriaLabel]="'sections.tour.skip' | translate"
      [mobileFullscreen]="false"
      [showCloseButton]="false"
      size="md"
    >
      <div
        class="tour"
        data-testid="onboarding-tour"
        role="region"
        aria-roledescription="carousel"
        [attr.aria-label]="'sections.tour.title' | translate"
      >
        <div
          class="tour-slide"
          role="group"
          aria-roledescription="slide"
          aria-live="polite"
          [attr.aria-label]="'sections.tour.step' | translate: { current: index() + 1, total: slides.length }"
          [attr.data-testid]="'tour-slide-' + slide().section"
        >
          <span class="tour-icon" aria-hidden="true"><crwd-icon [name]="slide().icon" size="32px" /></span>
          <h3 class="tour-title">{{ 'sections.' + slide().section + '.title' | translate }}</h3>
          <p class="tour-explain">{{ 'sections.' + slide().section + '.explain' | translate }}</p>
        </div>

        <div class="tour-dots">
          @for (s of slides; track s.section; let i = $index) {
            <button
              type="button"
              class="tour-dot"
              data-testid="tour-dot"
              [class.active]="i === index()"
              [attr.aria-current]="i === index() ? 'step' : null"
              [attr.aria-label]="'sections.tour.go-to' | translate: { section: ('sections.' + s.section + '.title' | translate) }"
              (click)="goTo(i)"
            ></button>
          }
        </div>
      </div>

      <crwd-button modal-footer variant="ghost" data-testid="tour-skip" (pressed)="finish('skip')">
        {{ 'sections.tour.skip' | translate }}
      </crwd-button>
      @if (index() > 0) {
        <crwd-button modal-footer variant="outline" data-testid="tour-back" (pressed)="back()">
          {{ 'sections.tour.back' | translate }}
        </crwd-button>
      }
      @if (isLast()) {
        <crwd-button modal-footer variant="primary" data-testid="tour-done" (pressed)="finish('done')">
          {{ 'sections.tour.done' | translate }}
        </crwd-button>
      } @else {
        <crwd-button modal-footer variant="primary" data-testid="tour-next" (pressed)="next()">
          {{ 'sections.tour.next' | translate }}
        </crwd-button>
      }
    </crwd-modal>
  `,
  styles: `
    .tour { display: flex; flex-direction: column; align-items: center; gap: 1.25rem; padding: 0.5rem 0 0.25rem; }
    .tour-slide { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 0.5rem; min-height: 11rem; max-width: 28rem; }
    .tour-icon {
      display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; border-radius: 1rem;
      background: color-mix(in srgb, var(--primary-color, #6366f1) 12%, transparent); color: var(--primary-color, currentColor);
    }
    .tour-title { margin: 0.5rem 0 0; font-size: 1.25rem; font-weight: 600; }
    .tour-explain { margin: 0; line-height: 1.55; color: var(--text-secondary-color, inherit); }
    .tour-dots { display: flex; gap: 0; }
    /* 24 x 24 px hit area (WCAG 2.2 target size); the visible dot is ::before. */
    .tour-dot {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 1.5rem; height: 1.5rem; padding: 0 0.25rem; border: 0; border-radius: 999px; cursor: pointer;
      background: transparent;
    }
    .tour-dot::before {
      content: ''; display: block; width: 0.625rem; height: 0.625rem; border-radius: 999px;
      background: var(--border-color, rgba(127, 127, 127, 0.35));
    }
    .tour-dot.active::before { width: 1.5rem; background: var(--primary-color, currentColor); }
    .tour-dot:focus-visible { outline: 2px solid var(--primary-color, currentColor); outline-offset: 0; }
  `,
})
export class OnboardingTourComponent implements OnInit {
  protected readonly tour = inject(OnboardingTourService);

  readonly slides = TOUR_SLIDES;
  readonly index = signal(0);
  readonly slide = computed(() => this.slides[this.index()]);
  readonly isLast = computed(() => this.index() === this.slides.length - 1);

  constructor() {
    // Every opening starts where the service says (slide 1 for both entry points).
    effect(() => {
      if (this.tour.open()) this.index.set(this.tour.startIndex());
    });
  }

  ngOnInit(): void {
    this.tour.start();
  }

  next(): void {
    if (!this.isLast()) this.index.update((i) => i + 1);
  }

  back(): void {
    if (this.index() > 0) this.index.update((i) => i - 1);
  }

  goTo(i: number): void {
    if (i >= 0 && i < this.slides.length) this.index.set(i);
  }

  /**
   * "Get started" (`done`) or Skip — and Esc / ✕ through openChange (`skip`).
   * The service tells them apart for the Help guide. When "Get started" closes
   * the modal, the kit's openChange(false) echo lands after the tour is already
   * closed, so it never turns a `done` into a `skip`.
   */
  finish(exit: TourExit): void {
    this.tour.close(exit);
  }

  onOpenChange(open: boolean): void {
    if (!open) this.finish('skip');
  }

  /**
   * ← / → inside the tour dialog only (review T3): a document-wide
   * preventDefault would also swallow arrows meant for anything else on the
   * page (another overlay, a field). The kit traps focus in the dialog, so
   * every arrow meant for the carousel starts there. Esc is the kit's.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.tour.open()) return;
    const target = event.target as Element | null;
    // The tour's own dialog container (the kit renders it in an overlay).
    const dialog = target?.closest?.('crwd-dialog-container');
    if (!dialog?.querySelector('[data-testid="onboarding-tour"]')) return;
    if (event.key === 'ArrowRight') {
      this.next();
      event.preventDefault();
    } else if (event.key === 'ArrowLeft') {
      this.back();
      event.preventDefault();
    }
  }
}
